import React, { useState, FormEvent } from "react";
import axios, { AxiosError } from "axios";

interface PasswordChangeProps {
  onDone?: () => void;
}

function EyeIcon({ visible }: { visible: boolean }) {
  if (visible) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </svg>
    );
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

export function PasswordField({ id, label, value, onChange, placeholder }: {
  id: string; label: string; value: string;
  onChange: (v: string) => void; placeholder?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      <div style={{ position: "relative" }}>
        <input
          type={show ? "text" : "password"}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          placeholder={placeholder}
          style={{ paddingRight: "2.5rem", width: "100%", boxSizing: "border-box" }}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          aria-label={show ? "Hide password" : "Show password"}
          style={{
            position: "absolute", right: "0.5rem", top: "50%",
            transform: "translateY(-50%)", background: "none",
            border: "none", cursor: "pointer", padding: "0.25rem",
            color: "#666", display: "flex", alignItems: "center",
          }}
        >
          <EyeIcon visible={show} />
        </button>
      </div>
    </div>
  );
}

function PasswordChange({ onDone }: PasswordChangeProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Set by Login.tsx when the API returns passwordResetRequired=true.
  const isForced = localStorage.getItem("passwordResetRequired") === "true";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setServerErrors([]);
    setSuccess("");

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match — check both fields carefully.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setLoading(true);
    try {
      const body: Record<string, string> = { newPassword };
      if (!isForced) body.currentPassword = currentPassword;

      const response = await axios.post<{ success: boolean; message: string }>(
        "/api/auth/change-password",
        body
      );
      if (response.data.success) {
        setSuccess("Password changed! Redirecting to login...");
        localStorage.removeItem("passwordResetRequired");
        setTimeout(() => {
          if (onDone) onDone();
          window.location.href = "/login";
        }, 2000);
      }
    } catch (err) {
      const axiosError = err as AxiosError<{ error?: string; errors?: string[] }>;
      const msg = axiosError.response?.data?.error || "Password change failed. Please try again.";
      const errs = axiosError.response?.data?.errors;
      setError(msg);
      if (errs?.length) setServerErrors(errs);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <img src="/hoa-logo.png" alt="Horn of Africa" style={{ height: 90, display: "block", margin: "0 auto 20px" }} />
        <h2>{isForced ? "Password Reset Required" : "Change Password"}</h2>
        {isForced && (
          <p style={{ marginBottom: "20px", color: "#666" }}>
            You must change your password before continuing.
          </p>
        )}
        <form onSubmit={handleSubmit}>
          {!isForced && (
            <PasswordField id="currentPassword" label="Current Password:"
              value={currentPassword} onChange={setCurrentPassword}
              placeholder="Enter your current password" />
          )}
          <PasswordField id="newPassword" label="New Password:"
            value={newPassword} onChange={setNewPassword}
            placeholder="Enter new password" />
          <PasswordField id="confirmPassword" label="Confirm New Password:"
            value={confirmPassword} onChange={setConfirmPassword}
            placeholder="Type new password again" />

          {error && (
            <div className="error-message">
              {error}
              {serverErrors.length > 0 && (
                <ul style={{ marginTop: "6px", paddingLeft: "18px", fontSize: "13px" }}>
                  {serverErrors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          )}

          {success && (
            <div style={{ backgroundColor: "#d4edda", color: "#155724", padding: "10px", borderRadius: "5px", marginBottom: "10px" }}>
              {success}
            </div>
          )}

          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? "Changing Password..." : "Change Password"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default PasswordChange;
